import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createHostAdapter } from "../shared/host/adapter";
import { applyTheme, syncHostChrome } from "../shared/host/theme";
import {
  correlationIdOf,
  createApiClient,
  isSafeInternalRoute,
  type AssistantJobResponse,
  type BillingOptions,
  type DraftCompatibility,
  type ProposalResponse,
  type SchemaChange,
  type SchemaDiagnostic,
} from "../shared/api/client";
import type {
  AbilityRecord,
  Bootstrap,
  CompiledManifest,
  ControlCommand,
} from "../shared/api/generated/contracts";
import { LocaleProvider, useT } from "../shared/i18n";
import {
  Banner,
  Button,
  DetailHeader,
  ErrorState,
  Sheet,
  Skeleton,
  ToastProvider,
} from "../shared/ui";
import { validateManifest } from "../shared/validation/manifest";
import { parseRoute, routeHref, routeKey } from "./router";
import { StartupScreen, TopBar, type StartupPhase } from "./Shell";
import {
  AbilityTitle,
  AppTabBar,
  TAB_BAR_CLEARANCE,
  type MenuView,
  type TabId,
} from "./AppChrome";
import { AppErrorBoundary, RecoveryScreen } from "./Recovery";
import {
  RecoveryError,
  botLink,
  isPendingDeletion,
  isSessionLoss,
  recoveryFor,
  runsLocally,
  type RecoveryKind,
} from "./recoveryKinds";
import { dismissIntro, introDismissed } from "./introMemory";
import { lastSection, rememberSection, sectionVisits } from "./sectionMemory";
import { FirstRun } from "../features/sections/FirstRun";
import { FirstRunIntro } from "../features/sections/FirstRunIntro";
import { EditSectionSheet } from "../features/sections/EditSectionSheet";
import { SectionList } from "../features/sections/SectionList";
import { homeSectionFor } from "../features/sections/sections";
import { ManifestView } from "../features/renderer/registry";
import { collectionsWithCreateControl } from "../features/renderer/commands";
import { PreviewRecordOverlay } from "../features/preview/overlay";
import { PrintSurface } from "../features/print/PrintSurface";
import { ProposalReview } from "../features/proposal-review/ProposalReview";
import { AssistantJobStatus } from "../features/assistant-job/AssistantJobStatus";
import { AdminConsole } from "../features/admin/AdminConsole";
import type { OperationsSnapshot } from "../features/admin/operations";
import {
  AdminEntry,
  StandaloneAdmin,
  isOperationsSnapshot,
  runOperatorLogin,
} from "../features/admin-entry";
import { PendingDeletion } from "../features/profile/PendingDeletion";
import { Profile, type ProfileSnapshot } from "../features/profile/Profile";
import { pinTimezone, timezonePinned } from "../features/profile/timezonePin";
import { CollectionEditor } from "../features/records/CollectionEditor";
import { Reminders } from "../features/reminders/Reminders";
import { exchangeTelegramSession } from "../features/session/session";
import { BlockedChanges } from "../features/draft/BlockedChanges";
import { DraftActionBar } from "../features/draft/DraftActionBar";
import { backfillLines } from "../features/draft/changes";
import { DraftChanges } from "../features/draft/DraftChanges";
import {
  BackfillNotice,
  StaleDraftBanner,
} from "../features/draft/DraftNotices";
import { DraftPublishDialog } from "../features/draft/DraftPublishDialog";
import { InterfaceDraftBar } from "../features/draft/InterfaceDraftBar";
import {
  discardInterfaceDraft,
  isInterfaceEditDraft,
} from "../features/draft/interfaceDraft";

const fallbackManifest: CompiledManifest = {
  manifest_version: "1.0",
  schema_format_version: "1.0",
  compiler_version: "1.0",
  registry_version: "1",
  checksum: "local",
  collections: [],
  views: [
    {
      id: "home",
      label: "Workspace Home",
      role: "home",
      show_in_navigation: true,
      context: { kind: "none" },
      sections: [
        {
          id: "intro",
          columns: 1,
          elements: [
            {
              id: "welcome",
              kind: "heading",
              config: { text: "Welcome to Mioscape" },
            },
            {
              id: "copy",
              kind: "text",
              config: { text: "Your private Workspace is ready." },
            },
          ],
        },
      ],
    },
  ],
  actions: [],
  print_forms: [],
};
interface DraftPreview {
  abilityId: string;
  manifest: CompiledManifest;
  revision: number;
  baseVersionId?: string | null;
  /** Server change list, backfill plan and stale flag (AGE-007). */
  changes?: SchemaChange[];
  compatibility?: DraftCompatibility | null;
  baseVersionNumber?: number | null;
  stale?: boolean;
}
const fallbackBootstrap: Bootstrap = {
  session: {
    user_id: "local",
    workspace_id: "local",
    expires_at: "2099-01-01T00:00:00Z",
  },
  abilities: [{ id: "local", name: "Workspace", manifest: fallbackManifest }],
};

export function App() {
  const host = useMemo(() => createHostAdapter(), []);
  // Outside Telegram the admin route is a standalone browser app; it skips the
  // Telegram bootstrap entirely. Decided once from the entry path.
  const [standalone] = useState(
    () =>
      host.kind === "local" &&
      parseRoute(window.location.pathname + window.location.search).kind ===
        "admin",
  );
  // Telegram's language until the stored profile choice arrives.
  const [languageTag, setLanguageTag] = useState(() => host.languageCode());
  return (
    <LocaleProvider languageTag={languageTag}>
      <AppErrorBoundary>
        {standalone ? (
          <StandaloneAdmin host={host} />
        ) : (
          <AppShell onLanguage={setLanguageTag} />
        )}
      </AppErrorBoundary>
    </LocaleProvider>
  );
}

function AppShell({ onLanguage }: { onLanguage: (tag: string) => void }) {
  const t = useT();
  const host = useMemo(() => createHostAdapter(), []);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<RecoveryKind | null>(null);
  const [recoveryCorrelationId, setRecoveryCorrelationId] = useState<
    string | undefined
  >(undefined);
  // Set once the server says this workspace awaits purge (purgeAt may follow).
  const [pendingDeletion, setPendingDeletion] = useState<{
    purgeAt: string | null;
  } | null>(null);
  const showPendingDeletion = useCallback(() => {
    setPendingDeletion((current) => current ?? { purgeAt: null });
    void createApiClient()
      .accountStatus()
      .then((status) => {
        if (status.state === "pending_deletion")
          setPendingDeletion({ purgeAt: status.purge_at });
      })
      .catch(() => undefined);
  }, []);
  const [phase, setPhase] = useState<StartupPhase>("host_initializing");
  const [path, setPath] = useState(
    window.location.pathname + window.location.search,
  );
  const [navigationToken] = useState(() =>
    new URLSearchParams(window.location.search).get("nav"),
  );
  const [theme, setTheme] = useState<"light" | "dark">(host.colorScheme());
  const startupRequest = useRef<
    | Promise<{
        value: Bootstrap;
        target: string | null;
        navigationFailed: boolean;
        navigationError: unknown;
      }>
    | undefined
  >(undefined);
  const [draftPreview, setDraftPreview] = useState<DraftPreview | null>(null);
  // Bumped to refetch the draft; the notice explains why it just changed.
  const [previewReload, setPreviewReload] = useState(0);
  const [staleDraftNotice, setStaleDraftNotice] = useState(false);
  // True while the reload that the stale notice itself triggered is in flight.
  const staleReloadPending = useRef(false);
  const raiseStaleDraftNotice = () => {
    staleReloadPending.current = true;
    setStaleDraftNotice(true);
    setPreviewReload((count) => count + 1);
  };
  const [publishStep, setPublishStep] = useState<"confirm" | "running" | null>(
    null,
  );
  // Keyed by draft revision: a changed draft deserves a fresh attempt.
  const [publishBlocked, setPublishBlocked] = useState<{
    revision: number;
    changes: SchemaDiagnostic[];
  } | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  // Set when this session restored a prior version, so publishing says so.
  const [rollbackAbilityId, setRollbackAbilityId] = useState<string | null>(
    null,
  );
  const [, setOverlayRevision] = useState(0);
  // A create_record command opens the editor in a sheet over the view.
  const [pendingCreate, setPendingCreate] = useState<{
    collectionId: string;
    defaults: Record<string, unknown>;
  } | null>(null);
  const [proposal, setProposal] = useState<ProposalResponse | null>(null);
  const [proposalLoadError, setProposalLoadError] = useState<{
    id: string;
    message: string;
    correlationId?: string;
  } | null>(null);
  const [assistantJob, setAssistantJob] = useState<AssistantJobResponse | null>(
    null,
  );
  const [assistantJobLoadError, setAssistantJobLoadError] = useState<{
    id: string;
    message: string;
    correlationId?: string;
  } | null>(null);
  const overlay = useMemo(() => new PreviewRecordOverlay(), []);
  const route = parseRoute(path);
  // A pending create belongs to the screen it was opened on.
  const currentRouteKey = routeKey(route);
  const [pendingRouteKey, setPendingRouteKey] = useState(currentRouteKey);
  if (pendingRouteKey !== currentRouteKey) {
    setPendingRouteKey(currentRouteKey);
    setPendingCreate(null);
  }
  const [profile, setProfile] = useState<ProfileSnapshot | null>(null);
  const [billingOptions, setBillingOptions] = useState<BillingOptions | null>(
    null,
  );
  const [introSeen, setIntroSeen] = useState(false);
  const [operatorSnapshot, setOperatorSnapshot] =
    useState<OperationsSnapshot | null>(null);
  const [operatorStatus, setOperatorStatus] = useState<
    "idle" | "loading" | "ready" | "signin" | "denied" | "error"
  >("idle");
  // Bumped when the sign-in handoff succeeds so the snapshot is fetched again.
  const [operatorAttempt, setOperatorAttempt] = useState(0);
  const [handoffPhase, setHandoffPhase] = useState<
    "idle" | "waiting" | "failed"
  >("idle");
  const handoffAbort = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      handoffAbort.current?.abort();
    },
    [],
  );
  useEffect(() => {
    if (!bootstrap || host.kind === "local") return;
    let active = true;
    void createApiClient()
      .profile(bootstrap.session.user_id)
      .then((value) => {
        if (!active) return;
        setProfile(value);
        if (value.locale) onLanguage(value.locale);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (isPendingDeletion(cause)) showPendingDeletion();
        else if (isSessionLoss(cause)) setRecovery("auth_expired");
        else
          setError(
            cause instanceof Error
              ? cause.message
              : "Profile could not be loaded.",
          );
      });
    return () => {
      active = false;
    };
  }, [bootstrap, host, onLanguage, showPendingDeletion]);
  const onProfileRoute = route.kind === "profile";
  useEffect(() => {
    if (!bootstrap || host.kind === "local" || !onProfileRoute) return;
    let active = true;
    // Options only add an optional entry, so a failure leaves it hidden.
    void createApiClient()
      .billingOptions()
      .then((value) => {
        if (active) setBillingOptions(value);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [bootstrap, host, onProfileRoute]);
  // A section can be published from the bot chat while the Mini App stays
  // open in the background; without this it keeps the startup snapshot until a
  // forced reload. Refresh quietly whenever the app comes back to the front
  // and every time the owner opens the Abilities page.
  const hasBootstrap = bootstrap !== null;
  const onAbilitiesPage = route.kind === "abilities";
  useEffect(() => {
    if (!hasBootstrap || host.kind === "local") return;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void createApiClient()
        .bootstrap()
        .then((next) => {
          setBootstrap((current) => {
            if (!current) return current;
            const key = (value: Bootstrap) =>
              value.abilities
                .map((item) => `${item.id}:${String(item.schema_version_id)}`)
                .join("|");
            return key(current) === key(next) ? current : next;
          });
        })
        .catch(() => undefined);
    };
    if (onAbilitiesPage) refresh();
    document.addEventListener("visibilitychange", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [hasBootstrap, host, onAbilitiesPage]);
  // The session probe runs on every route so Profile can offer "enter" as
  // soon as a valid operator cookie exists. The route is read through a ref so
  // navigating to admin does not restart an in-flight probe.
  const onAdminRoute = useRef(false);
  const isAdminRoute = route.kind === "admin";
  useEffect(() => {
    onAdminRoute.current = isAdminRoute;
  }, [isAdminRoute]);
  useEffect(() => {
    if (!bootstrap) return;
    if (host.kind === "local" || profile?.is_operator !== true) return;
    let active = true;
    void fetch("/operator/operations/snapshot", { credentials: "include" })
      .then(async (response) => {
        if (response.status === 401 || response.status === 403) {
          if (active) setOperatorStatus("signin");
          return;
        }
        if (!response.ok)
          throw new Error(`operator_http_${String(response.status)}`);
        const value: unknown = await response.json();
        if (!isOperationsSnapshot(value))
          throw new Error("operator_snapshot_invalid");
        if (active) {
          setOperatorSnapshot(value);
          setOperatorStatus("ready");
        }
      })
      .catch(() => {
        // Off the admin route a failed probe just leaves the sign-in variant.
        if (active)
          setOperatorStatus(onAdminRoute.current ? "error" : "signin");
      });
    return () => {
      active = false;
    };
  }, [bootstrap, host, profile, operatorAttempt]);
  const previewAbilityId =
    route.kind === "preview" ? route.abilityId : undefined;
  const activeDraftPreview =
    host.kind === "local" && previewAbilityId
      ? {
          abilityId: previewAbilityId,
          manifest: fallbackManifest,
          revision: 1,
        }
      : draftPreview?.abilityId === previewAbilityId
        ? draftPreview
        : null;
  const [loadedRecords, setLoadedRecords] = useState<{
    abilityId: string;
    records: AbilityRecord[];
  } | null>(null);
  const homeSection = bootstrap
    ? homeSectionFor(bootstrap.abilities, lastSection())
    : undefined;
  const recordAbility =
    route.kind === "home"
      ? homeSection
      : bootstrap?.abilities.find(
          (item) =>
            (route.kind === "ability" ||
              route.kind === "record" ||
              route.kind === "print") &&
            item.id === route.abilityId,
        );
  const recordsAbilityId =
    route.kind === "preview"
      ? activeDraftPreview?.abilityId
      : recordAbility?.id;
  const recordsManifest =
    route.kind === "preview"
      ? activeDraftPreview?.manifest
      : recordAbility?.manifest;
  useEffect(() => {
    if (
      !bootstrap ||
      !recordsAbilityId ||
      !recordsManifest ||
      host.kind === "local"
    )
      return;
    let active = true;
    const api = createApiClient();
    const load = async () => {
      const pages = await Promise.all(
        recordsManifest.collections.map(async (collection) => {
          const records: AbilityRecord[] = [];
          for (let offset = 0; offset <= 100_000; offset += 100) {
            const page = await api.records(
              bootstrap.session.workspace_id,
              recordsAbilityId,
              collection.id,
              offset,
            );
            records.push(...page);
            if (page.length < 100) break;
          }
          return records;
        }),
      );
      if (active)
        setLoadedRecords({
          abilityId: recordsAbilityId,
          records: pages.flat(),
        });
    };
    void load().catch((cause: unknown) => {
      if (!active) return;
      if (isPendingDeletion(cause)) showPendingDeletion();
      else if (isSessionLoss(cause)) setRecovery("auth_expired");
      else
        setError(
          cause instanceof Error
            ? cause.message
            : "Records could not be loaded.",
        );
    });
    return () => {
      active = false;
    };
  }, [bootstrap, recordsAbilityId, recordsManifest, host, showPendingDeletion]);
  const proposalId = route.kind === "proposal" ? route.proposalId : undefined;
  const assistantJobId =
    route.kind === "assistantJob" ? route.jobId : undefined;
  const proposalError =
    proposalId && (host.kind === "local" || !host.initData())
      ? t("proposal.unavailable")
      : proposalLoadError && proposalLoadError.id === proposalId
        ? proposalLoadError.message
        : null;
  const assistantJobError =
    assistantJobId && (host.kind === "local" || !host.initData())
      ? t("assistantJob.unavailable")
      : assistantJobLoadError && assistantJobLoadError.id === assistantJobId
        ? assistantJobLoadError.message
        : null;
  useEffect(() => {
    host.ready();
    host.expand();
    return host.onThemeChange(setTheme);
  }, [host]);
  useEffect(() => {
    // theme-boot.js already painted the first frame; this keeps the document in
    // step with later host changes and pushes the two surfaces Telegram owns.
    applyTheme(theme);
    syncHostChrome(host, theme);
  }, [host, theme]);
  useEffect(() => {
    let active = true;
    const onPopState = () => {
      setPath(window.location.pathname + window.location.search);
    };
    window.addEventListener("popstate", onPopState);
    const load = async () => {
      if (host.kind === "local") {
        if (!runsLocally()) throw new RecoveryError("host_unsupported");
        if (active) setBootstrap(fallbackBootstrap);
        return;
      }
      const initData = host.initData();
      if (!initData) throw new RecoveryError("host_unsupported");
      if (active) setPhase("authenticating");
      startupRequest.current ??= (async () => {
        const session = await exchangeTelegramSession(initData);
        if (session.status === "unavailable")
          throw new Error("The Telegram session service is unavailable.");
        if (session.status !== "authenticated")
          throw new RecoveryError("session_rejected");
        const api = createApiClient();
        const value = await api.bootstrap();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!timezone) throw new Error("The device timezone is unavailable.");
        if (!timezonePinned()) await api.setTimezone(timezone);
        const validated = value.abilities.every(
          (ability) => validateManifest(ability.manifest).ok,
        );
        if (!validated) throw new RecoveryError("render_error");
        if (value.abilities.length === 0)
          throw new RecoveryError("backend_error");
        let target: string | null = null;
        let navigationFailed = false;
        let navigationError: unknown = null;
        if (navigationToken) {
          try {
            target = await api.redeemNavigation(
              navigationToken,
              value.session.user_id,
            );
          } catch (cause: unknown) {
            navigationFailed = true;
            navigationError = cause;
          }
        }
        return { value, target, navigationFailed, navigationError };
      })();
      const result = await startupRequest.current;
      if (!active) return;
      if (result.target && isSafeInternalRoute(result.target)) {
        setPhase("resolving_target");
        window.history.replaceState({}, "", result.target);
        setPath(result.target);
      } else if (result.navigationFailed || result.target) {
        window.history.replaceState({}, "", "/");
        setPath("/");
        setRecovery("nav_token_expired");
      }
      setPhase("loading_route");
      setBootstrap(result.value);
    };
    void load().catch((cause: unknown) => {
      if (!active) return;
      if (isPendingDeletion(cause)) {
        showPendingDeletion();
        return;
      }
      setRecoveryCorrelationId(correlationIdOf(cause));
      setRecovery(recoveryFor(cause));
    });
    return () => {
      active = false;
      window.removeEventListener("popstate", onPopState);
    };
  }, [host, navigationToken, showPendingDeletion]);
  useEffect(() => {
    if (!previewAbilityId || !bootstrap || host.kind === "local") return;
    const initData = host.initData();
    if (!initData) return;

    let active = true;
    void createApiClient()
      .preview(previewAbilityId)
      .then((draft) => {
        if (!validateManifest(draft.manifest).ok)
          throw new Error("The Draft manifest is unsupported.");
        if (active) {
          setDraftPreview({
            abilityId: previewAbilityId,
            manifest: draft.manifest,
            revision: draft.draftRevision,
            baseVersionId: draft.baseVersionId,
            changes: draft.changes,
            compatibility: draft.compatibility,
            baseVersionNumber: draft.baseVersionNumber,
            stale: draft.stale,
          });
          // The reload that raised the notice keeps it; any later load clears.
          if (staleReloadPending.current) staleReloadPending.current = false;
          else setStaleDraftNotice(false);
        }
      })
      .catch((cause: unknown) => {
        if (active && isSessionLoss(cause)) setRecovery("auth_expired");
        else if (active) {
          setDraftPreview(null);
          setError(
            cause instanceof Error
              ? cause.message
              : "The Draft preview could not be loaded.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [bootstrap, host, previewAbilityId, previewReload]);
  useEffect(() => {
    if (!proposalId || !bootstrap || host.kind === "local") return;
    const initData = host.initData();
    if (!initData) return;

    let active = true;
    void createApiClient()
      .proposal(proposalId, bootstrap.session.user_id)
      .then((value) => {
        if (active) {
          setProposal(value);
          setProposalLoadError(null);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setProposal(null);
          setProposalLoadError({
            id: proposalId,
            correlationId: correlationIdOf(cause),
            message:
              cause instanceof Error
                ? cause.message
                : "The Proposal could not be loaded.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [bootstrap, host, proposalId, t]);
  useEffect(() => {
    if (!assistantJobId || !bootstrap || host.kind === "local") return;
    const initData = host.initData();
    if (!initData) return;

    let active = true;
    void createApiClient()
      .assistantJob(assistantJobId, bootstrap.session.user_id)
      .then((value) => {
        if (active) {
          setAssistantJob(value);
          setAssistantJobLoadError(null);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setAssistantJob(null);
          setAssistantJobLoadError({
            id: assistantJobId,
            correlationId: correlationIdOf(cause),
            message:
              cause instanceof Error
                ? cause.message
                : "The Assistant request could not be loaded.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [assistantJobId, bootstrap, host, t]);
  const visitedSection =
    route.kind === "ability" || route.kind === "record"
      ? route.abilityId
      : undefined;
  useEffect(() => {
    if (
      visitedSection &&
      bootstrap?.abilities.some((item) => item.id === visitedSection)
    )
      rememberSection(visitedSection);
  }, [bootstrap, visitedSection]);
  const navigate = (href: string, options?: { replace?: boolean }) => {
    if (options?.replace) window.history.replaceState({}, "", href);
    else window.history.pushState({}, "", href);
    setPath(href);
  };
  const startOperatorLogin = async () => {
    // Only one handoff loop at a time: a new attempt cancels the previous.
    handoffAbort.current?.abort();
    const controller = new AbortController();
    handoffAbort.current = controller;
    setHandoffPhase("waiting");
    try {
      const outcome = await runOperatorLogin({
        openExternal: host.openExternal,
        failureMessage: t("admin.loadFailed"),
        signal: controller.signal,
      });
      if (outcome === "aborted") return;
      if (outcome === "authenticated") {
        setHandoffPhase("idle");
        setOperatorStatus("loading");
        setOperatorAttempt((value) => value + 1);
        // The snapshot effect only runs on the admin route; sign-in can start
        // from Profile, so land on admin (without duplicating history).
        if (window.location.pathname !== routeHref({ kind: "admin" }))
          navigate(routeHref({ kind: "admin" }));
      } else {
        setHandoffPhase("failed");
        setOperatorStatus(outcome === "forbidden" ? "denied" : "signin");
      }
    } catch (cause: unknown) {
      if (controller.signal.aborted) return;
      setHandoffPhase("idle");
      throw cause;
    }
  };
  if (pendingDeletion)
    return (
      <PendingDeletion
        api={createApiClient()}
        purgeAt={pendingDeletion.purgeAt}
        onRestored={() => {
          // A fresh load re-runs the session exchange and the bootstrap.
          window.location.assign("/");
        }}
      />
    );
  if (recovery) {
    const link = botLink();
    const action: Record<RecoveryKind, (() => void) | undefined> = {
      host_unsupported: link
        ? () => {
            window.location.assign(link);
          }
        : undefined,
      session_rejected: host.close,
      auth_expired: () => {
        // A fresh load re-runs the Telegram session exchange.
        window.location.assign("/");
      },
      nav_token_expired: () => {
        setRecovery(null);
        navigate("/");
      },
      // Retry and restart preserve the canonical deep link they started from.
      offline: () => {
        window.location.reload();
      },
      backend_error: () => {
        window.location.reload();
      },
      render_error: () => {
        window.location.reload();
      },
    };
    return (
      <RecoveryScreen
        kind={recovery}
        onAction={action[recovery]}
        correlationId={recoveryCorrelationId}
      />
    );
  }
  // There is no white screen and no endless loader anywhere in startup: a
  // failure above replaces this with its recovery screen.
  if (!bootstrap) return <StartupScreen phase={phase} />;
  const effectiveOperatorStatus =
    host.kind === "local" || profile?.is_operator === false
      ? "denied"
      : profile === null
        ? error
          ? "error"
          : "loading"
        : operatorStatus;
  if (route.kind === "admin")
    return (
      <ToastProvider closeLabel={t("app.close")}>
        <div className="min-h-screen bg-canvas text-fg">
          <TopBar eyebrow={t("app.administration")} onClose={host.close} />
          {effectiveOperatorStatus === "ready" && operatorSnapshot ? (
            <AdminConsole
              snapshot={operatorSnapshot}
              path={path}
              onNavigate={navigate}
              onSignIn={() => {
                void startOperatorLogin().catch(() => {
                  setOperatorStatus("error");
                });
              }}
            />
          ) : effectiveOperatorStatus === "loading" ||
            effectiveOperatorStatus === "idle" ? (
            <main className="p-gutter" aria-busy="true">
              <Skeleton height={160} />
            </main>
          ) : (
            <ErrorState
              title={t("admin.title")}
              body={
                effectiveOperatorStatus === "denied"
                  ? t("admin.noAccess")
                  : handoffPhase === "waiting"
                    ? t("admin.handoffWaiting")
                    : handoffPhase === "failed"
                      ? t("admin.handoffFailed")
                      : t("admin.loadFailed")
              }
              action={
                effectiveOperatorStatus === "signin" ? (
                  <Button
                    onClick={() => {
                      void startOperatorLogin().catch(() => {
                        setOperatorStatus("error");
                      });
                    }}
                  >
                    {t("admin.signIn")}
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      </ToastProvider>
    );
  const routeAbilityId =
    route.kind === "ability" ||
    route.kind === "record" ||
    route.kind === "preview" ||
    route.kind === "print"
      ? route.abilityId
      : undefined;
  const firstRun = route.kind === "home" && homeSection === undefined;
  const ability =
    route.kind === "home"
      ? homeSection
      : (bootstrap.abilities.find((item) => item.id === routeAbilityId) ??
        (routeAbilityId ? undefined : bootstrap.abilities[0]));
  const openSection = (item: (typeof bootstrap.abilities)[number]) => {
    navigate(
      routeHref({
        kind: "ability",
        abilityId: item.id,
        viewId:
          item.manifest.views.find((candidate) => candidate.role === "home")
            ?.id ?? "home",
      }),
    );
  };
  const interfaceEdit = isInterfaceEditDraft(
    host.kind !== "local",
    activeDraftPreview?.baseVersionId,
  );
  const manifest =
    route.kind === "preview"
      ? (activeDraftPreview?.manifest ?? fallbackManifest)
      : (ability?.manifest ?? fallbackManifest);
  const viewId =
    route.kind === "ability" || route.kind === "record"
      ? route.viewId
      : (manifest.views.find((view) => view.role === "home")?.id ?? "home");
  const view =
    manifest.views.find((item) => item.id === viewId) ??
    (route.kind === "ability" || route.kind === "record"
      ? undefined
      : manifest.views[0]);
  if (!view || (routeAbilityId && route.kind !== "preview" && !ability))
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-6">
        <ErrorState
          title={t("error.notFound.title")}
          body={t("error.noView")}
        />
      </main>
    );
  const backfill = backfillLines(
    activeDraftPreview?.compatibility,
    activeDraftPreview?.manifest,
  );
  const blockedChanges =
    publishBlocked && publishBlocked.revision === activeDraftPreview?.revision
      ? publishBlocked.changes
      : null;
  // The ability's own views (the former section switcher). The home view is
  // the screen itself, so it is not listed.
  const menuViews: MenuView[] = manifest.views
    .filter((item) => item.show_in_navigation && item.role !== "home")
    .map((item) => ({
      id: item.id,
      label: item.label,
      active:
        (route.kind === "ability" || route.kind === "record") &&
        route.viewId === item.id,
      onSelect: () => {
        navigate(
          routeHref({
            kind: "ability",
            abilityId: ability?.id ?? "local",
            viewId: item.id,
          }),
        );
      },
    }));
  // Collections the view already offers a create control for need no stacked
  // editor; the others keep theirs.
  const createControlled = collectionsWithCreateControl(view);
  const stackedCollections = manifest.collections.filter(
    (collection) => !createControlled.has(collection.id),
  );
  const createCollection = pendingCreate
    ? manifest.collections.find(
        (item) => item.id === pendingCreate.collectionId,
      )
    : undefined;
  const handleCommand = (command: ControlCommand) => {
    if (command.kind === "create_record")
      setPendingCreate({
        collectionId: command.collection_id,
        defaults: command.defaults ?? {},
      });
    else if (command.kind === "print" && routeAbilityId)
      navigate(
        routeHref({
          kind: "print",
          abilityId: routeAbilityId,
          formId: command.print_form_id,
        }),
      );
  };
  const selectedRecord =
    route.kind === "record" && loadedRecords?.abilityId === ability?.id
      ? loadedRecords?.records.find((record) => record.id === route.recordId)
      : undefined;
  const selectedCollection = manifest.collections.find(
    (collection) => collection.id === selectedRecord?.collection_id,
  );
  const previewRecords =
    route.kind === "preview" &&
    loadedRecords !== null &&
    activeDraftPreview !== null &&
    loadedRecords.abilityId === activeDraftPreview.abilityId
      ? overlay.apply(loadedRecords.records)
      : [];
  const selectedTitleValue =
    selectedRecord && selectedCollection
      ? selectedRecord.payload[selectedCollection.primary_display_field_id]
      : undefined;
  const selectedTitle =
    typeof selectedTitleValue === "string" ||
    typeof selectedTitleValue === "number" ||
    typeof selectedTitleValue === "boolean"
      ? String(selectedTitleValue)
      : (selectedCollection?.label ?? t("error.notFound.title"));
  const detailTitle =
    route.kind === "record"
      ? selectedTitle
      : route.kind === "preview"
        ? t("print.preview")
        : route.kind === "proposal"
          ? t("proposal.title")
          : route.kind === "assistantJob"
            ? t("assistantJob.title")
            : route.kind === "print"
              ? t("print.title")
              : t("app.workspace");
  const backHref =
    route.kind === "record" && ability
      ? routeHref({
          kind: "ability",
          abilityId: ability.id,
          viewId: route.viewId,
        })
      : "/";
  const operatorEntry = (
    <AdminEntry
      state={{
        is_operator: profile?.is_operator === true,
        can_enter: operatorStatus === "ready",
      }}
      waiting={handoffPhase === "waiting"}
      failed={handoffPhase === "failed"}
      onEnter={() => {
        navigate(routeHref({ kind: "admin" }));
      }}
      onSignIn={() => {
        void startOperatorLogin().catch(() => {
          setOperatorStatus("error");
        });
      }}
    />
  );
  const tabBar = (active: TabId) => (
    <AppTabBar
      active={active}
      onHome={() => {
        navigate(routeHref({ kind: "home" }));
      }}
      onSections={() => {
        navigate("/abilities");
      }}
      onAssistant={host.close}
      onProfile={() => {
        navigate("/profile");
      }}
    />
  );
  const canEditSection =
    ability !== undefined && !ability.provider_key && host.kind !== "local";
  const abilityTitle = (
    <AbilityTitle
      name={ability?.name ?? t("app.workspace")}
      system={Boolean(ability?.provider_key)}
      views={menuViews}
      onEdit={
        canEditSection
          ? () => {
              setEditSheetOpen(true);
            }
          : undefined
      }
    />
  );
  const tabId: TabId | null =
    route.kind === "home" || route.kind === "ability"
      ? "home"
      : route.kind === "profile"
        ? "profile"
        : null;
  if (route.kind === "abilities")
    return (
      <>
        <SectionList
          sections={bootstrap.abilities}
          visits={sectionVisits()}
          onOpen={openSection}
          onCreate={host.close}
          onBackToChat={host.kind === "local" ? undefined : host.close}
          onOpenDraft={(section) => {
            navigate(routeHref({ kind: "preview", abilityId: section.id }));
          }}
          history={
            host.kind === "local"
              ? undefined
              : {
                  api: createApiClient(),
                  onDraftReady: (section) => {
                    setRollbackAbilityId(section.id);
                    navigate(
                      routeHref({ kind: "preview", abilityId: section.id }),
                    );
                  },
                }
          }
          keywords={
            host.kind === "local"
              ? undefined
              : {
                  api: createApiClient(),
                  onDraftReady: (section) => {
                    navigate(
                      routeHref({ kind: "preview", abilityId: section.id }),
                    );
                  },
                }
          }
          onRename={
            host.kind === "local"
              ? undefined
              : async (section, name) => {
                  const api = createApiClient();
                  await api.renameAbility(section.id, name);
                  setBootstrap(await api.bootstrap());
                }
          }
        />
        {tabBar("sections")}
      </>
    );
  if (firstRun)
    return (
      <div
        className={`flex min-h-screen flex-col bg-canvas text-fg ${TAB_BAR_CLEARANCE}`}
      >
        {introDismissed(bootstrap.session.user_id) || introSeen ? (
          <FirstRun
            onChoose={() => {
              navigate("/abilities");
            }}
            onCreate={host.close}
          />
        ) : (
          <FirstRunIntro
            onStart={() => {
              dismissIntro(bootstrap.session.user_id);
              setIntroSeen(true);
              host.close();
            }}
            onSkip={() => {
              dismissIntro(bootstrap.session.user_id);
              setIntroSeen(true);
            }}
          />
        )}
        {tabBar("home")}
      </div>
    );
  if (
    ability?.provider_key === "system.ability.reminders" &&
    (route.kind === "home" || route.kind === "ability")
  ) {
    return (
      <div className={`min-h-screen bg-canvas text-fg ${TAB_BAR_CLEARANCE}`}>
        {abilityTitle}
        <Reminders api={createApiClient()} />
        {tabBar("home")}
      </div>
    );
  }
  return (
    <ToastProvider closeLabel={t("app.close")}>
      <div
        className={`flex min-h-screen flex-col bg-canvas text-fg ${tabId ? TAB_BAR_CLEARANCE : ""}`}
      >
        {route.kind === "home" || route.kind === "ability" ? (
          abilityTitle
        ) : route.kind === "profile" ? null : (
          <DetailHeader
            largeTitle
            title={detailTitle}
            eyebrow={
              route.kind === "preview"
                ? [ability?.name, activeDraftPreview?.manifest.views[0]?.label]
                    .filter(
                      (part, index, parts) =>
                        Boolean(part) && parts.indexOf(part) === index,
                    )
                    .join(" · ") || undefined
                : undefined
            }
            backLabel={t("app.back")}
            onBack={() => {
              navigate(backHref);
            }}
          />
        )}
        {ability && !ability.provider_key && host.kind !== "local" && (
          <EditSectionSheet
            open={editSheetOpen}
            onOpenChange={setEditSheetOpen}
            sectionName={ability.name}
            draft={ability.draft}
            onBackToChat={host.close}
            onOpenDraft={() => {
              setEditSheetOpen(false);
              navigate(routeHref({ kind: "preview", abilityId: ability.id }));
            }}
            history={{
              api: createApiClient(),
              abilityId: ability.id,
              onDraftReady: () => {
                setEditSheetOpen(false);
                setRollbackAbilityId(ability.id);
                navigate(routeHref({ kind: "preview", abilityId: ability.id }));
              },
            }}
            keywords={{
              api: createApiClient(),
              abilityId: ability.id,
              onDraftReady: () => {
                setEditSheetOpen(false);
                navigate(routeHref({ kind: "preview", abilityId: ability.id }));
              },
            }}
          />
        )}
        {error && (
          <div className="mx-auto w-full max-w-5xl px-gutter pt-3">
            <Banner tone="error">{error}</Banner>
          </div>
        )}
        <main
          className={`mx-auto flex w-full max-w-5xl flex-1 flex-col px-gutter pt-5 ${
            route.kind === "preview" || route.kind === "proposal"
              ? "pb-0"
              : "pb-24"
          }`}
        >
          {route.kind === "record" ? (
            loadedRecords?.abilityId !== ability?.id ? (
              <Skeleton height={96} />
            ) : selectedRecord &&
              selectedCollection &&
              ability?.schema_version_id &&
              (view.context.kind !== "record" ||
                view.context.collection_id === selectedCollection.id) ? (
              <CollectionEditor
                key={`${ability.id}:${selectedRecord.id}`}
                api={createApiClient()}
                scope={{
                  workspaceId: bootstrap.session.workspace_id,
                  abilityId: ability.id,
                  actorId: bootstrap.session.user_id,
                  schemaVersionId: ability.schema_version_id,
                }}
                collection={selectedCollection}
                collections={manifest.collections}
                records={loadedRecords?.records ?? []}
                selectedRecord={selectedRecord}
                onDeleted={(id) => {
                  setLoadedRecords((current) =>
                    current
                      ? {
                          ...current,
                          records: current.records.filter(
                            (record) => record.id !== id,
                          ),
                        }
                      : current,
                  );
                  navigate(
                    routeHref({
                      kind: "ability",
                      abilityId: ability.id,
                      viewId,
                    }),
                  );
                }}
                onSaved={(saved) => {
                  setLoadedRecords((current) =>
                    current
                      ? {
                          ...current,
                          records: current.records.map((record) =>
                            record.id === saved.id ? saved : record,
                          ),
                        }
                      : current,
                  );
                  navigate(
                    routeHref({
                      kind: "ability",
                      abilityId: ability.id,
                      viewId,
                    }),
                  );
                }}
              />
            ) : (
              <ErrorState
                title={t("error.notFound.title")}
                body={t("error.noView")}
              />
            )
          ) : route.kind === "profile" ? (
            profile ? (
              <Profile
                snapshot={profile}
                onUpgrade={async (idempotencyKey) => {
                  const api = createApiClient();
                  const url = await api.startProCheckout(idempotencyKey);
                  await new Promise<void>((resolve, reject) => {
                    const timer = window.setTimeout(() => {
                      reject(new Error("Payment confirmation timed out."));
                    }, 120_000);
                    host.openInvoice(url, (status) => {
                      window.clearTimeout(timer);
                      if (status === "paid") resolve();
                      else reject(new Error("Payment was not completed."));
                    });
                  });
                  setProfile(await api.profile(bootstrap.session.user_id));
                }}
                onTimezone={async (zone) => {
                  await createApiClient().setTimezone(zone);
                  pinTimezone();
                  setProfile({ ...profile, timezone: zone });
                }}
                onLocale={async (locale) => {
                  await createApiClient().setLocale(
                    bootstrap.session.user_id,
                    locale,
                  );
                  setProfile({ ...profile, locale });
                  onLanguage(locale);
                }}
                operatorEntry={operatorEntry}
                accountApi={createApiClient()}
                onAccountDeleted={() => {
                  // The server revoked the session; start over restricted.
                  window.location.assign("/");
                }}
                billingOptions={billingOptions}
                onPaySbp={async (idempotencyKey, productId) => {
                  const { checkoutUrl, orderId } =
                    await createApiClient().startSbpCheckout(
                      idempotencyKey,
                      productId,
                    );
                  host.openExternal(checkoutUrl);
                  return orderId;
                }}
                onCheckOrder={async (orderId) =>
                  (await createApiClient().billingOrder(orderId)).state
                }
                onRefresh={async () => {
                  setProfile(
                    await createApiClient().profile(bootstrap.session.user_id),
                  );
                }}
              />
            ) : (
              <Skeleton height={96} />
            )
          ) : route.kind === "assistantJob" ? (
            assistantJobError ? (
              <ErrorState
                title={t("assistantJob.title")}
                body={assistantJobError}
                correlationId={assistantJobLoadError?.correlationId}
                correlationLabel={t("error.reference")}
                copyLabel={t("error.reference.copy")}
                copiedLabel={t("error.reference.copied")}
              />
            ) : assistantJob === null || assistantJob.id !== assistantJobId ? (
              <div className="flex flex-col gap-3" aria-busy="true">
                <Skeleton height={36} />
                <Skeleton height={96} />
                <span className="sr-only">{t("startup.loadingRoute")}</span>
              </div>
            ) : (
              <AssistantJobStatus
                job={assistantJob}
                onCancel={async () => {
                  const initData = host.initData();
                  if (!initData)
                    throw new Error("Telegram session is unavailable.");
                  const updated = await createApiClient().cancelAssistantJob(
                    assistantJob.id,
                    bootstrap.session.user_id,
                  );
                  setAssistantJob(updated);
                }}
                onFeedback={
                  bootstrap.feedback_enabled
                    ? async (body) => {
                        const initData = host.initData();
                        if (!initData)
                          throw new Error("Telegram session is unavailable.");
                        return createApiClient().submitJobFeedback(
                          assistantJob.id,
                          bootstrap.session.user_id,
                          body,
                        );
                      }
                    : undefined
                }
                onRetry={async () => {
                  const initData = host.initData();
                  if (!initData)
                    throw new Error("Telegram session is unavailable.");
                  const retried = await createApiClient().retryAssistantJob(
                    assistantJob.id,
                    bootstrap.session.user_id,
                  );
                  setAssistantJob(retried);
                  navigate(
                    routeHref({ kind: "assistantJob", jobId: retried.id }),
                  );
                }}
              />
            )
          ) : route.kind === "proposal" ? (
            proposalError ? (
              <ErrorState
                title={t("proposal.title")}
                body={proposalError}
                correlationId={proposalLoadError?.correlationId}
                correlationLabel={t("error.reference")}
                copyLabel={t("error.reference.copy")}
                copiedLabel={t("error.reference.copied")}
              />
            ) : proposal === null || proposal.id !== proposalId ? (
              <div className="flex flex-col gap-3" aria-busy="true">
                <Skeleton height={36} />
                <Skeleton height={96} />
                <span className="sr-only">{t("startup.loadingRoute")}</span>
              </div>
            ) : (
              <ProposalReview
                proposal={{
                  id: proposal.id,
                  status: proposal.status,
                  changes: proposal.summary.map((change) =>
                    change.record_id
                      ? `${change.operation}: ${change.record_id}`
                      : change.operation,
                  ),
                }}
                onAccept={async () => {
                  const initData = host.initData();
                  if (!initData)
                    throw new Error("Telegram session is unavailable.");
                  const updated = await createApiClient().confirmProposal(
                    proposal.id,
                    bootstrap.session.user_id,
                  );
                  setProposal(updated);
                }}
                onReject={async () => {
                  const initData = host.initData();
                  if (!initData)
                    throw new Error("Telegram session is unavailable.");
                  const updated = await createApiClient().rejectProposal(
                    proposal.id,
                    bootstrap.session.user_id,
                  );
                  setProposal(updated);
                }}
              />
            )
          ) : route.kind === "print" ? (
            <PrintSurface
              manifest={manifest}
              formId={route.formId}
              records={
                loadedRecords?.abilityId === ability?.id
                  ? (loadedRecords?.records ?? [])
                  : []
              }
              mode="print"
              onPrint={host.print}
              canPrint={host.canPrint()}
              onOpenExternal={() => {
                host.openExternal(window.location.href);
              }}
              onAskAssistant={host.close}
            />
          ) : route.kind === "preview" && activeDraftPreview === null ? (
            // The skeleton reserves the shape of the draft view, so nothing
            // shifts once the manifest lands.
            <div className="flex flex-col gap-3" aria-busy="true">
              <Skeleton height={36} />
              <Skeleton height={70} />
              <Skeleton height={70} />
              <span className="sr-only">{t("startup.loadingRoute")}</span>
            </div>
          ) : route.kind === "preview" ? (
            <div className="flex flex-col gap-3.5">
              <Banner
                tone="draft"
                action={
                  <Button
                    size="sm"
                    variant="tinted"
                    onClick={() => {
                      overlay.clear();
                      setOverlayRevision((revision) => revision + 1);
                    }}
                  >
                    {t("draft.reset")}
                  </Button>
                }
              >
                <strong>{t("draft.title")}</strong>{" "}
                <span>
                  {t("draft.revisionLabel")} {activeDraftPreview?.revision}.
                  {!interfaceEdit && ` ${t("draft.banner")}`}
                </span>
              </Banner>
              {interfaceEdit && (
                <div>
                  <InterfaceDraftBar
                    discard={() =>
                      discardInterfaceDraft(
                        createApiClient(),
                        route.abilityId,
                        activeDraftPreview?.revision,
                      )
                    }
                    onStale={raiseStaleDraftNotice}
                    onDiscarded={() => {
                      setStaleDraftNotice(false);
                      navigate(
                        routeHref({
                          kind: "ability",
                          abilityId: route.abilityId,
                          viewId,
                        }),
                      );
                    }}
                  />
                </div>
              )}
              {staleDraftNotice && (
                <div>
                  <Banner tone="warning">{t("draft.stale")}</Banner>
                </div>
              )}
              {host.kind !== "local" && activeDraftPreview?.stale && (
                <div>
                  <StaleDraftBanner
                    discard={async () => {
                      await discardInterfaceDraft(
                        createApiClient(),
                        route.abilityId,
                      );
                      navigate(
                        routeHref({
                          kind: "ability",
                          abilityId: route.abilityId,
                          viewId,
                        }),
                      );
                    }}
                  />
                </div>
              )}
              {backfill.length > 0 && (
                <div>
                  <BackfillNotice lines={backfill} />
                </div>
              )}
              {host.kind !== "local" && activeDraftPreview && (
                <div>
                  <DraftChanges
                    changes={activeDraftPreview.changes ?? []}
                    manifest={activeDraftPreview.manifest}
                    baseVersionNumber={activeDraftPreview.baseVersionNumber}
                  />
                </div>
              )}
              {blockedChanges && (
                <BlockedChanges
                  changes={blockedChanges}
                  manifests={[ability?.manifest, activeDraftPreview?.manifest]}
                />
              )}
              <ManifestView
                view={view}
                hideTitle
                fabAboveActionBar
                context={{
                  onCommand: handleCommand,
                  mode: "preview",
                  abilityId: route.abilityId,
                  records: previewRecords,
                  navigate,
                  collections: manifest.collections,
                }}
              />
              <section className="mt-4 flex flex-col gap-4">
                {stackedCollections.map((collection) => (
                  <CollectionEditor
                    key={`preview:${route.abilityId}:${collection.id}`}
                    api={createApiClient()}
                    scope={{
                      workspaceId: bootstrap.session.workspace_id,
                      abilityId: route.abilityId,
                      actorId: bootstrap.session.user_id,
                      schemaVersionId: "preview-local-only",
                    }}
                    collection={collection}
                    collections={manifest.collections}
                    records={previewRecords}
                    localOnly
                    onSaved={(record) => {
                      overlay.update(record);
                      setOverlayRevision((revision) => revision + 1);
                    }}
                    onDeleted={(id) => {
                      const record = previewRecords.find(
                        (candidate) => candidate.id === id,
                      );
                      if (record) overlay.delete(record);
                      setOverlayRevision((revision) => revision + 1);
                    }}
                  />
                ))}
              </section>
              <DraftActionBar
                block={
                  activeDraftPreview?.stale === true
                    ? "stale"
                    : blockedChanges !== null
                      ? "blocked"
                      : null
                }
                onReset={() => {
                  overlay.clear();
                  setOverlayRevision((revision) => revision + 1);
                }}
                onPublish={() => {
                  setPublishStep("confirm");
                }}
              />
              {activeDraftPreview && (
                <DraftPublishDialog
                  open={publishStep !== null}
                  onOpenChange={(open) => {
                    setPublishStep(open ? "confirm" : null);
                  }}
                  api={createApiClient()}
                  abilityId={route.abilityId}
                  actorId={bootstrap.session.user_id}
                  draft={{
                    manifest: activeDraftPreview.manifest,
                    draftRevision: activeDraftPreview.revision,
                  }}
                  changeCount={
                    host.kind === "local" ||
                    activeDraftPreview.baseVersionNumber == null
                      ? null
                      : (activeDraftPreview.changes ?? []).length
                  }
                  hasBackfill={backfill.length > 0}
                  reason={
                    rollbackAbilityId === route.abilityId
                      ? "rollback"
                      : "user_confirmed"
                  }
                  onPublished={async () => {
                    setStaleDraftNotice(false);
                    setBootstrap(await createApiClient().bootstrap());
                    navigate(
                      routeHref({
                        kind: "ability",
                        abilityId: route.abilityId,
                        viewId,
                      }),
                    );
                  }}
                  onStaleRevision={raiseStaleDraftNotice}
                  onBlocked={(changes) => {
                    setPublishBlocked({
                      revision: activeDraftPreview.revision,
                      changes,
                    });
                  }}
                  onFailed={(message) => {
                    setError(message);
                    // The server may have learned something (stale base):
                    // reload the Draft so the page shows it.
                    setPreviewReload((count) => count + 1);
                  }}
                />
              )}
            </div>
          ) : (
            <>
              <ManifestView
                view={view}
                hideTitle
                fabAboveActionBar
                context={{
                  onCommand: handleCommand,
                  mode: "published",
                  abilityId: ability?.id,
                  records:
                    loadedRecords?.abilityId === ability?.id
                      ? (loadedRecords?.records ?? [])
                      : [],
                  navigate,
                  collections: manifest.collections,
                }}
              />
              {ability && manifest.print_forms.length > 0 && (
                <section
                  aria-label={t("print.title")}
                  className="mt-4 flex flex-wrap gap-2"
                >
                  {manifest.print_forms.map((form, index) => {
                    const formId =
                      typeof form.id === "string" ? form.id : String(index);
                    const label =
                      typeof form.label === "string"
                        ? form.label
                        : t("print.showPreview");
                    return (
                      <Button
                        key={formId}
                        variant="secondary"
                        onClick={() => {
                          navigate(
                            routeHref({
                              kind: "print",
                              abilityId: ability.id,
                              formId,
                            }),
                          );
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </section>
              )}
            </>
          )}
        </main>
        {(route.kind === "ability" || route.kind === "home") &&
          stackedCollections.length > 0 &&
          ability?.schema_version_id &&
          loadedRecords?.abilityId === ability.id && (
            <section className="flex flex-col gap-gutter p-4">
              {stackedCollections.map((collection) => (
                <CollectionEditor
                  key={`${ability.id}:${collection.id}`}
                  api={createApiClient()}
                  scope={{
                    workspaceId: bootstrap.session.workspace_id,
                    abilityId: ability.id,
                    actorId: bootstrap.session.user_id,
                    schemaVersionId: ability.schema_version_id ?? "",
                  }}
                  collection={collection}
                  collections={manifest.collections}
                  records={loadedRecords.records}
                  onDeleted={(id) => {
                    setLoadedRecords((current) =>
                      current
                        ? {
                            ...current,
                            records: current.records.filter(
                              (record) => record.id !== id,
                            ),
                          }
                        : current,
                    );
                  }}
                  onSaved={(saved) => {
                    setLoadedRecords((current) =>
                      current?.abilityId === ability.id
                        ? {
                            abilityId: ability.id,
                            records: [
                              ...current.records.filter(
                                (item) => item.id !== saved.id,
                              ),
                              saved,
                            ],
                          }
                        : current,
                    );
                  }}
                />
              ))}
            </section>
          )}
        {createCollection && pendingCreate && (
          <Sheet
            open
            onOpenChange={(open) => {
              if (!open) setPendingCreate(null);
            }}
            title={`${t("controls.create.title")}: ${createCollection.label}`}
          >
            <CollectionEditor
              key={createCollection.id}
              api={createApiClient()}
              scope={{
                workspaceId: bootstrap.session.workspace_id,
                abilityId: routeAbilityId ?? ability?.id ?? "",
                actorId: bootstrap.session.user_id,
                schemaVersionId:
                  route.kind === "preview"
                    ? "preview-local-only"
                    : (ability?.schema_version_id ?? ""),
              }}
              collection={createCollection}
              collections={manifest.collections}
              records={
                route.kind === "preview"
                  ? previewRecords
                  : (loadedRecords?.records ?? [])
              }
              localOnly={route.kind === "preview"}
              createDefaults={pendingCreate.defaults}
              onClose={() => {
                setPendingCreate(null);
              }}
              onSaved={(saved) => {
                if (route.kind === "preview") {
                  overlay.update(saved);
                  setOverlayRevision((revision) => revision + 1);
                  return;
                }
                setLoadedRecords((current) =>
                  current && current.abilityId === ability?.id
                    ? {
                        abilityId: current.abilityId,
                        records: [
                          ...current.records.filter(
                            (item) => item.id !== saved.id,
                          ),
                          saved,
                        ],
                      }
                    : current,
                );
              }}
            />
          </Sheet>
        )}
        {tabId && tabBar(tabId)}
      </div>
    </ToastProvider>
  );
}
